import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import {
  useBatchResolveCardsMutation,
  parseDecklistText,
  ResolvedCard,
  UnresolvedCard,
  BatchResolveResponse,
  groupCardsByCategory,
  CATEGORY_ORDER,
  calculateDeckValue,
} from 'store/services/cards/decklistApi';
import { DecklistAttrs, DecklistCardEntry } from './TipTapExtensions';
import './DecklistImportModal.css';

// MTG Formats list
const MTG_FORMATS = [
  'Standard',
  'Pioneer',
  'Modern',
  'Legacy',
  'Vintage',
  'Pauper',
  'Commander',
  'Brawl',
  'Historic',
  'Alchemy',
  'Explorer',
  'Timeless',
  'Premodern',
  'Old School',
  'Oathbreaker',
  'Penny Dreadful',
  'Limited',
  'Sealed',
  'Draft',
  'Cube',
  'Other',
];

type TabType = 'import' | 'preview';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (attrs: DecklistAttrs) => void;
  initialData?: DecklistAttrs | null;
}

interface ValidationResult {
  mainboard: {
    resolved: ResolvedCard[];
    unresolved: UnresolvedCard[];
  };
  sideboard: {
    resolved: ResolvedCard[];
    unresolved: UnresolvedCard[];
  };
}

export default function DecklistImportModal({
  isOpen,
  onClose,
  onSave,
  initialData,
}: Props) {
  // Form state
  const [title, setTitle] = useState(initialData?.title || '');
  const [author, setAuthor] = useState(initialData?.author || '');
  const [format, setFormat] = useState(initialData?.format || 'Standard');
  const [mainboardText, setMainboardText] = useState('');
  const [sideboardText, setSideboardText] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('import');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  // RTK Query
  const [batchResolve] = useBatchResolveCardsMutation();

  // Initialize from existing data
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setAuthor(initialData.author || '');
      setFormat(initialData.format || 'Standard');

      // Convert card entries back to text format
      const mainboardLines = (initialData.mainboard || []).map(
        (card) => `${card.quantity} ${card.name}${card.setCode ? ` [${card.setCode.toUpperCase()}]` : ''}`
      );
      const sideboardLines = (initialData.sideboard || []).map(
        (card) => `${card.quantity} ${card.name}${card.setCode ? ` [${card.setCode.toUpperCase()}]` : ''}`
      );

      setMainboardText(mainboardLines.join('\n'));
      setSideboardText(sideboardLines.join('\n'));
    }
  }, [initialData]);

  // Reset state on close
  const handleClose = useCallback(() => {
    setTitle('');
    setAuthor('');
    setFormat('Standard');
    setMainboardText('');
    setSideboardText('');
    setActiveTab('import');
    setValidationResult(null);
    onClose();
  }, [onClose]);

  // Parse card counts
  const parsedCounts = useMemo(() => {
    const mainboard = parseDecklistText(mainboardText);
    const sideboard = parseDecklistText(sideboardText);
    return {
      mainboardCards: mainboard.reduce((sum, card) => sum + (card.quantity || 0), 0),
      sideboardCards: sideboard.reduce((sum, card) => sum + (card.quantity || 0), 0),
      mainboardUniqueCards: mainboard.length,
      sideboardUniqueCards: sideboard.length,
    };
  }, [mainboardText, sideboardText]);

  // Validate cards with backend
  const handleValidate = async () => {
    const mainboardParsed = parseDecklistText(mainboardText);
    const sideboardParsed = parseDecklistText(sideboardText);

    if (mainboardParsed.length === 0) {
      toast.error('Please enter at least one card in the mainboard');
      return;
    }

    // Check card limit
    const totalCards = mainboardParsed.length + sideboardParsed.length;
    if (totalCards > 500) {
      toast.error(`Maximum 500 unique cards per decklist. You have ${totalCards}.`);
      return;
    }

    setIsValidating(true);

    try {
      // Resolve mainboard
      const mainboardResult = await batchResolve({
        cards: mainboardParsed,
        includePrice: true,
      }).unwrap();

      // Resolve sideboard (if any)
      let sideboardResult: BatchResolveResponse = { resolved: [], unresolved: [], stats: { total: 0, resolvedCount: 0, unresolvedCount: 0 } };
      if (sideboardParsed.length > 0) {
        sideboardResult = await batchResolve({
          cards: sideboardParsed,
          includePrice: true,
        }).unwrap();
      }

      setValidationResult({
        mainboard: {
          resolved: mainboardResult.resolved,
          unresolved: mainboardResult.unresolved,
        },
        sideboard: {
          resolved: sideboardResult.resolved,
          unresolved: sideboardResult.unresolved,
        },
      });

      // Show results
      const totalResolved =
        mainboardResult.stats.resolvedCount + sideboardResult.stats.resolvedCount;
      const totalUnresolved =
        mainboardResult.stats.unresolvedCount + sideboardResult.stats.unresolvedCount;

      if (totalUnresolved > 0) {
        toast.warning(`${totalResolved} cards found, ${totalUnresolved} not found`);
      } else {
        toast.success(`All ${totalResolved} cards validated successfully!`);
      }

      // Switch to preview tab
      setActiveTab('preview');
    } catch (error: any) {
      console.error('Validation error:', error);
      if (error?.status === 429) {
        toast.error('Rate limit exceeded. Please wait a moment and try again.');
      } else {
        toast.error('Failed to validate cards. Please try again.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  // Save the decklist
  const handleSave = () => {
    if (!validationResult) {
      toast.error('Please validate the decklist first');
      return;
    }

    // Convert resolved cards to storage format
    const mainboardEntries: DecklistCardEntry[] = validationResult.mainboard.resolved.map(
      (card) => ({
        quantity: card.quantity,
        name: card.name,
        scryfallId: card.scryfallID,
        setCode: card.setCode,
      })
    );

    // Add unresolved cards (they'll display without links)
    validationResult.mainboard.unresolved.forEach((card) => {
      mainboardEntries.push({
        quantity: card.quantity,
        name: card.requestedName,
      });
    });

    const sideboardEntries: DecklistCardEntry[] = validationResult.sideboard.resolved.map(
      (card) => ({
        quantity: card.quantity,
        name: card.name,
        scryfallId: card.scryfallID,
        setCode: card.setCode,
      })
    );

    validationResult.sideboard.unresolved.forEach((card) => {
      sideboardEntries.push({
        quantity: card.quantity,
        name: card.requestedName,
      });
    });

    onSave({
      title: title || 'Untitled Deck',
      author: author || '',
      format: format || 'Unknown Format',
      mainboard: mainboardEntries,
      sideboard: sideboardEntries,
    });

    handleClose();
  };

  // Calculate preview stats
  const previewStats = useMemo(() => {
    if (!validationResult) return null;

    const allResolved = [
      ...validationResult.mainboard.resolved,
      ...validationResult.sideboard.resolved,
    ];

    return {
      totalValue: calculateDeckValue(allResolved),
      mainboardCount: validationResult.mainboard.resolved.reduce(
        (sum, card) => sum + card.quantity,
        0
      ) + validationResult.mainboard.unresolved.reduce(
        (sum, card) => sum + card.quantity,
        0
      ),
      sideboardCount: validationResult.sideboard.resolved.reduce(
        (sum, card) => sum + card.quantity,
        0
      ) + validationResult.sideboard.unresolved.reduce(
        (sum, card) => sum + card.quantity,
        0
      ),
    };
  }, [validationResult]);

  if (!isOpen) return null;

  return createPortal(
    <div className="decklist-modal-overlay" onClick={handleClose}>
      <div className="decklist-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="decklist-modal-header">
          <h2>{initialData ? 'Edit Decklist' : 'Add Decklist'}</h2>
          <button className="decklist-modal-close" onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        {/* Tabs */}
        <div className="decklist-modal-tabs">
          <button
            className={`decklist-tab ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            Import
          </button>
          <button
            className={`decklist-tab ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('preview')}
            disabled={!validationResult}
          >
            Preview
          </button>
        </div>

        {/* Body */}
        <div className="decklist-modal-body">
          {activeTab === 'import' ? (
            <>
              {/* Metadata fields */}
              <div className="decklist-metadata">
                <div className="decklist-field">
                  <label>Deck Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Mono-Red Burn"
                    maxLength={100}
                  />
                </div>

                <div className="decklist-field-row">
                  <div className="decklist-field">
                    <label>Author (optional)</label>
                    <input
                      type="text"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      placeholder="e.g., ProPlayer123"
                      maxLength={50}
                    />
                  </div>

                  <div className="decklist-field">
                    <label>Format</label>
                    <select value={format} onChange={(e) => setFormat(e.target.value)}>
                      {MTG_FORMATS.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Card lists */}
              <div className="decklist-cards-section">
                <div className="decklist-textarea-container">
                  <label>
                    Mainboard
                    <span className="decklist-card-count">
                      {parsedCounts.mainboardCards} cards
                    </span>
                  </label>
                  <textarea
                    className="decklist-textarea"
                    value={mainboardText}
                    onChange={(e) => setMainboardText(e.target.value)}
                    placeholder={`Enter cards, one per line:

4 Lightning Bolt
4 Monastery Swiftspear
4 Goblin Guide
4 Eidolon of the Great Revel

Supported formats:
• 4 Card Name
• 4x Card Name
• Card Name (defaults to 1)
• 4 Card Name [SET]`}
                    disabled={isValidating}
                  />
                </div>

                <div className="decklist-textarea-container">
                  <label>
                    Sideboard (optional)
                    <span className="decklist-card-count">
                      {parsedCounts.sideboardCards} cards
                    </span>
                  </label>
                  <textarea
                    className="decklist-textarea decklist-textarea-small"
                    value={sideboardText}
                    onChange={(e) => setSideboardText(e.target.value)}
                    placeholder={`2 Smash to Smithereens
3 Roiling Vortex
2 Exquisite Firecraft`}
                    disabled={isValidating}
                  />
                </div>
              </div>

              {/* Validation progress */}
              {isValidating && (
                <div className="decklist-progress">
                  <div className="decklist-progress-bar">
                    <div className="decklist-progress-fill" />
                  </div>
                  <p>Validating cards...</p>
                </div>
              )}
            </>
          ) : (
            /* Preview Tab */
            <div className="decklist-preview-content">
              {validationResult && (
                <>
                  {/* Preview Header */}
                  <div className="decklist-preview-header">
                    <div className="decklist-preview-title-row">
                      <h3>{title || 'Untitled Deck'}</h3>
                      <span className="decklist-preview-format-badge">{format}</span>
                    </div>
                    <div className="decklist-preview-meta-row">
                      {author && <span>by {author}</span>}
                      <span>{previewStats?.mainboardCount} cards</span>
                      {previewStats && previewStats.sideboardCount > 0 && (
                        <span>{previewStats.sideboardCount} sideboard</span>
                      )}
                      {previewStats && previewStats.totalValue !== null && (
                        <span className="decklist-preview-value">
                          ${previewStats.totalValue.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Unresolved warnings */}
                  {(validationResult.mainboard.unresolved.length > 0 ||
                    validationResult.sideboard.unresolved.length > 0) && (
                    <div className="decklist-unresolved-warning">
                      <FontAwesomeIcon icon="exclamation-triangle" />
                      <span>
                        {validationResult.mainboard.unresolved.length +
                          validationResult.sideboard.unresolved.length}{' '}
                        card(s) could not be found. They will appear without links.
                      </span>
                    </div>
                  )}

                  {/* Preview Cards */}
                  <div className="decklist-preview-lists">
                    {/* Mainboard */}
                    <div className="decklist-preview-column">
                      <h4>Mainboard</h4>
                      {CATEGORY_ORDER.map((category) => {
                        const cards = groupCardsByCategory(
                          validationResult.mainboard.resolved
                        )[category];
                        if (cards.length === 0) return null;

                        const categoryCount = cards.reduce(
                          (sum, card) => sum + card.quantity,
                          0
                        );

                        return (
                          <div key={category} className="decklist-preview-category">
                            <h5>
                              {category}s ({categoryCount})
                            </h5>
                            <ul>
                              {cards.map((card, idx) => (
                                <li key={`${card.scryfallID}-${idx}`}>
                                  <span className="card-qty">{card.quantity}</span>
                                  <span className="card-name">{card.name}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}

                      {/* Unresolved mainboard cards */}
                      {validationResult.mainboard.unresolved.length > 0 && (
                        <div className="decklist-preview-category unresolved">
                          <h5>Unresolved</h5>
                          <ul>
                            {validationResult.mainboard.unresolved.map((card, idx) => (
                              <li key={`unresolved-${idx}`} className="unresolved-card">
                                <span className="card-qty">{card.quantity}</span>
                                <span className="card-name">{card.requestedName}</span>
                                <FontAwesomeIcon
                                  icon="exclamation-circle"
                                  title={card.message}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Sideboard */}
                    {(validationResult.sideboard.resolved.length > 0 ||
                      validationResult.sideboard.unresolved.length > 0) && (
                      <div className="decklist-preview-column">
                        <h4>Sideboard</h4>
                        {CATEGORY_ORDER.map((category) => {
                          const cards = groupCardsByCategory(
                            validationResult.sideboard.resolved
                          )[category];
                          if (cards.length === 0) return null;

                          const categoryCount = cards.reduce(
                            (sum, card) => sum + card.quantity,
                            0
                          );

                          return (
                            <div key={category} className="decklist-preview-category">
                              <h5>
                                {category}s ({categoryCount})
                              </h5>
                              <ul>
                                {cards.map((card, idx) => (
                                  <li key={`${card.scryfallID}-${idx}`}>
                                    <span className="card-qty">{card.quantity}</span>
                                    <span className="card-name">{card.name}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}

                        {/* Unresolved sideboard cards */}
                        {validationResult.sideboard.unresolved.length > 0 && (
                          <div className="decklist-preview-category unresolved">
                            <h5>Unresolved</h5>
                            <ul>
                              {validationResult.sideboard.unresolved.map((card, idx) => (
                                <li key={`sb-unresolved-${idx}`} className="unresolved-card">
                                  <span className="card-qty">{card.quantity}</span>
                                  <span className="card-name">{card.requestedName}</span>
                                  <FontAwesomeIcon
                                    icon="exclamation-circle"
                                    title={card.message}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="decklist-modal-footer">
          <MagicButton label="Cancel" color="grey" onPress={handleClose} />

          {activeTab === 'import' ? (
            <MagicButton
              label={isValidating ? 'Validating...' : 'Validate & Preview'}
              color="purple"
              onPress={handleValidate}
              isDisabled={isValidating || parsedCounts.mainboardCards === 0}
              isLoading={isValidating}
            />
          ) : (
            <>
              <MagicButton
                label="Back to Edit"
                color="grey"
                onPress={() => setActiveTab('import')}
              />
              <MagicButton
                label="Insert Decklist"
                color="purple"
                onPress={handleSave}
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
